import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import {CdkDragEnter, CdkDragStart, CdkDragDrop, transferArrayItem} from '@angular/cdk/drag-drop';
import {catchError, finalize, map, take} from 'rxjs/operators';
import {throwError} from 'rxjs';

import {
    TaskView,
    EmployeeView,
    VisitList,
    DragDropService,
    EmployeeService,
    TaskIdInfo,
    RoutePlanningService,
    RoutesService,
    ManualRouteInformation,
    ProblemInstanceService
} from 'planner-shared';

import {RouteTaskView} from '../route-task-view.model';
import {UnallocatedTaskService} from './services/unallocated-task.service';
import {TaskColorService} from "../../../shared/services/task-color/task-color.service";
import {FeatureFlags, FeatureFlagService} from "@core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: 'app-route-planning-unallocated-tasks',
    templateUrl: './route-planning-unallocated-tasks.component.html',
    styleUrls: ['./route-planning-unallocated-tasks.component.scss'],
    standalone: false
})
export class RoutePlanningUnallocatedTasksComponent implements OnInit {
    private readonly dragDropService = inject(DragDropService);
    private readonly unallocatedTaskService = inject(UnallocatedTaskService);
    private readonly employeeService = inject(EmployeeService);
    private readonly routePlanningService = inject(RoutePlanningService);
    private readonly routesService = inject(RoutesService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly taskColorService = inject(TaskColorService);
    private readonly featureFlagService = inject(FeatureFlagService);

    @Input()
    public set unallocatedTasks(value: TaskIdInfo[]) {
        if (value) {
            if (this.independentSyncTasksFeatureEnabled === undefined) {
                this.featureFlagService.isEnabled(FeatureFlags.IndependentSyncTasks)
                    .pipe(
                        take(1),
                        untilDestroyed(this)
                    )
                    .subscribe({
                        next: flagValue => {
                            this.independentSyncTasksFeatureEnabled = flagValue;
                            this.loadTasks(value);
                        },
                        error: () => this.loadTasks(value)
                    });
            } else {
                this.loadTasks(value);
            }
        }
    }

    @Input() public employee: EmployeeView;
    @Input() public updating: boolean;
    @Input() public dragDisabled: boolean;
    @Input() public saving: boolean;
    @Input() public pdfView: boolean;
    public totalUnallocatedTasks: number;
    public loading: boolean;
    public tasks: RouteTaskView[];
    public isSplitUnallocated: boolean;

    @Output() public reloadRoutes = new EventEmitter<VisitList>();

    public restrictionId: string;
    public showRestrictionContextMenu: boolean;
    public showRestrictionContextMenuTopPosition: number;
    public showRestrictionContextMenuLeftPosition: number;
    public showRoster: boolean;
    public independentSyncTasksFeatureEnabled: boolean;

    ngOnInit(): void {
        this.loading = true;
        this.showRestrictionContextMenu = false;
        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .pipe()
            .subscribe(result => this.showRoster = result.valueOf());
        this.dragDropService.isDraggingDisabled().subscribe(value => this.dragDisabled = value);

        document.addEventListener('click', () => {
            this.showRestrictionContextMenu = false;
        });
    }

    public started(event: CdkDragStart<TaskView>): void {
        this.dragDropService.setDraggingDisabled(true);
        this.dragDropService.setDraggingTask(event.source.data);
    }

    public released(): void {
        this.dragDropService.setDraggingTask(null);
    }

    public entered(event: CdkDragEnter): void {
        if (event.item.data.isEmployeeTask) {
            this.dragDropService.resetPlaceholderPositionInDropContainer(event);
            this.dragDropService.disallowDragDrop(event);
            return;
        }

        this.dragDropService.resetPlaceholderPositionInCurrentContainer(event);
    }

    public taskDropped(event: CdkDragDrop<RouteTaskView[]>): void {

        if (event.previousContainer === event.container) {
            this.dragDropService.setDraggingDisabled(false);
            return;
        }

        transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);

        const task = <TaskIdInfo>{
            taskId: event.item.data.originalTaskId,
            routePlannerTaskId: event.item.data.routePlannerTaskId
        };
        const manualRouteInformationList = [
            <ManualRouteInformation>{
                taskIdInfo: task,
                moveToUnallocated: true
            }
        ];

        this.routePlanningService.employeeTaskUpdateStateChanged(true);
        this.updating = true;
        this.routesService
            .sendTaskChangeToMrp(manualRouteInformationList)
            .pipe(
                catchError(() => {
                    transferArrayItem(
                        event.container.data,
                        event.previousContainer.data,
                        event.currentIndex,
                        event.previousIndex
                    );
                    return throwError('Update routes order failed.');
                }),
                map(result => result.routes),
                finalize(() => {
                    this.routePlanningService.employeeTaskUpdateStateChanged(false);
                    this.updating = false;
                    this.dragDropService.setDraggingDisabled(false);
                })
            )
            .subscribe(routes => {
                this.routesService.updateAndVerifyResult(routes, event, () => {
                    this.employee.allocatedTasks.splice(event.previousIndex, 1);
                    this.loadTasks(routes.nonAllocatedTasksInfo);
                    this.reloadRoutes.emit(routes);
                });
            });
    }

    public loadTasks(unallocatedTasks: TaskIdInfo[]): void {
        this.loading = true;
        this.unallocatedTaskService
            .getUnallocatedTasks(unallocatedTasks)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe(tasks => {
                if (this.independentSyncTasksFeatureEnabled) {
                    this.estimateSyncTasksAllocation(tasks);
                }
                this.tasks = tasks;
                this.tasks.sort(this.compareTasksByType);
                this.totalUnallocatedTasks = this.tasks.length;
                this.isSplitUnallocated = this.tasks.some(task => task.isShiftSplitTask);
                this.taskColorService.getTaskColors().forEach(coloredTask => {
                    const uncoloredTask = this.tasks.find(routeTask => routeTask.taskId === coloredTask.taskId);
                    if (uncoloredTask) {
                        uncoloredTask.color = coloredTask.color;
                    }
                });
                return this.tasks;
            });

    }

    public openModalWindow(task: RouteTaskView, e) {
        e.preventDefault();
        this.showRestrictionContextMenu = true;
        this.restrictionId = task.originalTaskId;
    }

    public getMousePosition(e) {
        this.showRestrictionContextMenuTopPosition = e.layerY;
        this.showRestrictionContextMenuLeftPosition = e.layerX;
    }

    public openEmployeesRestrictionModal(): void {
        this.showRestrictionContextMenu = false;
        this.employeeService.openEmployeesRestrictionModal(this.restrictionId);
    }

    private compareTasksByType(a: RouteTaskView, b: RouteTaskView) {
        if (!a.isEmployeeTask && b.isEmployeeTask) {
            return -1;
        }
        if (a.isEmployeeTask && !b.isEmployeeTask) {
            return 1;
        }

        return 0;
    }

    private estimateSyncTasksAllocation(tasks: RouteTaskView[]) {
        const unallocatedSyncedTasks = tasks.filter(task => task.isSynced);
        unallocatedSyncedTasks.forEach(syncedTask => {
            const hasUnallocatedPair =
                unallocatedSyncedTasks
                    .some(task =>
                        task !== syncedTask &&
                        task.fromTime === syncedTask.fromTime &&
                        task.toTime === syncedTask.toTime &&
                        task.patientId === syncedTask.patientId);
            if (!hasUnallocatedPair) {
                tasks.find(task => task.taskId === syncedTask.taskId).singleSyncedTask = true;
            }
        });
    }
}
